<script setup>
import { PhxTransactionPanelSlider } from '@phx-styleguides/iwc';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import WishlistButton from '@maison/components/wishlist/WishlistButton.vue';
import ProductPrice from '@phoenix/components/common/ProductPrice.vue';
import { usePrice } from '@phoenix/composables/price';
import Bag from '@phoenix/components/Bag';
import { showPanel } from '@phoenix/helpers/panel-helper';
import { FEATURE_FLAG, getFeatureFlag } from '@phoenix/helpers/feature-flag-helper';
import { PhxDivider } from '@sg/src/View/Components/Atoms/Divider';
import '@sg/src/View/Components/Molecules/SpecsTable/specs-table.css';
import { PhxsgButton } from '@sg/src/View/Components/Atoms/Button';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  reference: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  images: {
    type: Array,
    required: true,
  },
  specsRows: {
    type: Array,
    required: true,
  },
  price: {
    type: Object,
    required: true,
  },
  url: {
    type: String,
    required: true,
  },
  boutiqueAppointmentUrl: {
    type: String,
    required: true,
  },
  boutiqueStockAvailabilityUrl: {
    type: String,
    required: true,
  },
  shouldDisplayAddToCart: {
    type: Boolean,
    default: false,
  },
  shouldDisplayEcomButtons: {
    type: Boolean,
    default: true,
  },
  addToBagOptions: {
    type: Object,
    default: () => ({}),
  },
  contactsPanelId: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();

const { localizedPrice } = usePrice();

const btnAddToBag = ref(null);

const slider = computed(() => ({
  id: `${props.id}-slider`,
  slides: props.images,
  label: t('Accessibility.imageSlider.ariaLabel'),
  class: 'slider--full-width',
  options: {
    threshold: 5,
    slidesPerView: 1,
    effect: 'fade',
    mousewheel: {
      forceToAxis: true,
    },
  },
}));

const bag = new Bag();

const addToBag = async () => {
  Toolkit.helpers.buttons.addLoadingState(btnAddToBag.value);
  await bag.add(props.reference, props.addToBagOptions);
  Toolkit.helpers.buttons.removeLoadingState(btnAddToBag.value);
};
</script>

<template>
  <div class="transaction-panel-step__content">
    <phx-transaction-panel-slider :id="id" :title="title" :slider="slider" />

    <div class="phxsg-specs-table">
      <phx-divider color="foreground" />

      <div v-for="(specsRow, index) in specsRows" :key="index" class="phxsg-specs-table__row title-caption">
        <span class="phxsg-specs-table__row-left">{{ specsRow.left }}</span>
        <span class="phxsg-specs-table__row-right">{{ specsRow.right }}</span>
        <phx-divider color="foreground" />
      </div>

      <div class="phxsg-specs-table__row title-caption md:hidden">
        <span class="phxsg-specs-table__row-left">
          <span class="cluster cluster--spacing-2">
            <a :href="url" class="btn btn--underline btn--fade">
              {{ t('TransactionPanel.productLinkLabel') }}
            </a>
          </span>
        </span>

        <phx-divider color="foreground" />
      </div>

      <div class="phxsg-specs-table__row title-caption md:hidden">
        <span class="phxsg-specs-table__row-left">
          <span class="cluster cluster--spacing-2">
            <a
              v-if="getFeatureFlag(FEATURE_FLAG.BOUTIQUE_STOCK_AVAILABILITY)"
              :href="boutiqueStockAvailabilityUrl"
              class="btn btn--underline btn--fade"
            >
              {{ t('Product.findInBoutique') }}
            </a>
          </span>
        </span>

        <span v-if="shouldDisplayEcomButtons" class="phxsg-specs-table__row-right">
          <span class="cluster cluster--spacing-2">
            <wishlist-button :reference="reference" type="text" />
          </span>
        </span>

        <phx-divider color="foreground" />
      </div>

      <div class="phxsg-specs-table__row title-caption max-sm:hidden">
        <span class="phxsg-specs-table__row-left">
          <span class="cluster cluster--spacing-2">
            <a :href="url" class="btn btn--underline btn--fade">
              {{ t('TransactionPanel.productLinkLabel') }}
            </a>
          </span>
        </span>

        <span class="phxsg-specs-table__row-right">
          <span class="cluster cluster--spacing-2">
            <template v-if="getFeatureFlag(FEATURE_FLAG.BOUTIQUE_STOCK_AVAILABILITY)">
              <a :href="boutiqueStockAvailabilityUrl" class="btn btn--underline btn--fade">
                {{ t('Product.findInBoutique') }}
              </a>
              |
            </template>
            <wishlist-button v-if="shouldDisplayEcomButtons" :reference="reference" type="text" />
          </span>
        </span>

        <phx-divider color="foreground" />
      </div>

      <div class="phxsg-specs-table__row title-caption">
        <span class="phxsg-specs-table__row-right">
          <product-price
            v-bind="localizedPrice(price.price)"
            :should-display-from-price="price.shouldDisplayFromPrice"
          />
        </span>

        <phx-divider color="foreground" />
      </div>
    </div>
  </div>

  <div v-if="shouldDisplayEcomButtons" class="transaction-panel-step__footer">
    <a :href="boutiqueAppointmentUrl" class="btn btn--tertiary btn--plain">
      <span class="btn__text">{{ t('CTA.bookAnAppointment') }}</span>
    </a>

    <PhxsgButton v-if="shouldDisplayAddToCart" ref="btnAddToBag" variant="plain" color="primary" @click="addToBag">
      {{ t('CTA.orderOnline') }}
    </PhxsgButton>

    <PhxsgButton v-else-if="contactsPanelId" variant="plain" color="tertiary" @click="showPanel(contactsPanelId)">
      {{ t('ContactPanel.buttonLabel') }}
    </PhxsgButton>
  </div>
</template>
